import { cva, type VariantProps } from 'class-variance-authority';
import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from './lib/cn';

const card = cva('bg-card text-card-foreground', {
  variants: {
    variant: {
      /** The standard surface: 1px border, 12px radius, whisper shadow. */
      default: 'rounded-xl border border-border shadow-card',
      /** Borderless, for nesting inside another card. */
      flat: 'rounded-lg',
      /** Tinted inner panel — the embedded meeting card inside a post. */
      inset: 'rounded-lg border border-border/60 bg-primary-subtle',
      /** Neutral inset — bot post key/value grid, quoted content. */
      'inset-neutral': 'rounded-lg bg-subtle',
      /** Fixed dark navy — incoming call / notification popups. */
      popup: 'rounded-2xl border border-popup-border bg-popup text-popup-foreground shadow-popup',
      /** Elevated white popup — message notification card. */
      'popup-light': 'rounded-2xl border border-border bg-card shadow-modal',
    },
    padding: {
      none: 'p-0',
      sm: 'p-3',
      md: 'p-4', //  16px — right-rail cards
      lg: 'p-5', //  20px — post cards
      xl: 'p-6',
    },
    interactive: {
      true: 'cursor-pointer transition-colors duration-fast hover:border-border-strong hover:bg-subtle',
      false: '',
    },
  },
  defaultVariants: { variant: 'default', padding: 'md', interactive: false },
});

export interface CardProps
  extends HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof card> {
  children: ReactNode;
  /** Renders as <article> / <section> etc. for correct semantics. */
  as?: 'div' | 'section' | 'article' | 'aside';
}

export function Card({
  children, as: Tag = 'div', variant, padding, interactive, className, ...props
}: CardProps) {
  return (
    <Tag className={cn(card({ variant, padding, interactive }), className)} {...props}>
      {children}
    </Tag>
  );
}

export interface CardHeaderProps {
  title: ReactNode;
  /** Right-aligned slot — usually a "See all" link or a "..." IconButton. */
  action?: ReactNode;
  className?: string;
}

/** Card title row: 15px semibold title, right-aligned action. */
export function CardHeader({ title, action, className }: CardHeaderProps) {
  return (
    <div className={cn('flex items-center justify-between gap-3', className)}>
      <h3 className="text-lg font-semibold text-foreground">{title}</h3>
      {action ? <div className="flex shrink-0 items-center gap-1">{action}</div> : null}
    </div>
  );
}
